import { useEffect } from 'react';
import { mutateCore } from './rpc';

/**
 * Le battement de présence du bureau : « je suis là, dans cette application, sur cet écran ».
 *
 * **Sans lui, l'écran de suivi de l'enseignant est un mensonge poli.** `teacher.presence`
 * (`packages/core-domain`) lit `core.presence` ; `presence.heartbeat` est la seule opération qui
 * écrive dans cette table ; et rien, dans le bureau, ne l'appelait — vérifié par recherche avant
 * d'écrire ce fichier, puis à l'écran : les cinq élèves de la classe de démonstration
 * apparaissaient tous « jamais connecté », y compris pendant qu'ils travaillaient dans le CRM. Le
 * suivi de séance ne pouvait donc RIEN montrer. C'est ce fichier qui le rend vrai.
 *
 * **`tabId` vit dans `sessionStorage`, jamais dans un `useState`.** `core.presence` a pour clé
 * primaire `(user_id, tab_id)` : un identifiant régénéré à chaque montage laisserait une ligne
 * morte derrière lui à chaque rechargement de page — un élève qui recharge trois fois
 * apparaîtrait comme trois onglets. `sessionStorage` est propre à l'onglet ET survit au
 * rechargement : exactement la durée de vie que cette clé doit avoir. Deux onglets réellement
 * ouverts restent deux lignes — c'est voulu, et `teacher.presence` ne retient que la plus fraîche.
 *
 * **Un échec de battement ne casse jamais le bureau.** Réseau coupé, session expirée, classe
 * refusée : ce battement est une commodité de suivi, pas une fonction dont dépend le travail de
 * l'élève. L'erreur est donc avalée ici, et nulle part ailleurs — c'est le seul appel réseau du
 * bureau dont l'échec doit rester silencieux.
 *
 * Cadence : 30 secondes, contre `PRESENCE_INACTIVE_AFTER_SECONDS = 120` côté cœur. Quatre
 * battements de marge avant qu'un élève ne bascule en « inactif » : une seule requête perdue (un
 * Wi-Fi de salle de classe) ne doit jamais le faire disparaître de l'écran de l'enseignant.
 */
const HEARTBEAT_MS = 30_000;
const TAB_ID_KEY = 'ndrc.tabId';

function resolveTabId(): string {
  try {
    const existing = sessionStorage.getItem(TAB_ID_KEY);
    if (existing !== null && existing !== '') return existing;
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(TAB_ID_KEY, fresh);
    return fresh;
  } catch {
    // Navigation privée stricte, stockage refusé par la configuration du navigateur : un
    // identifiant neuf vaut mieux qu'un bureau qui ne démarre pas.
    return crypto.randomUUID();
  }
}

export interface PresenceHeartbeat {
  /** La classe dans laquelle l'acteur travaille. `undefined` : rien n'est envoyé — un compte sans
   * classe n'a pas de séance à suivre, et `presence.heartbeat` exige un `classId` réel. */
  classId: string | undefined;
  /** L'app ouverte, ou `null` sur le bureau nu. */
  appId: string | null;
  /** L'écran exact, tel qu'il est dans l'URL. */
  route: string;
}

export function usePresenceHeartbeat({ classId, appId, route }: PresenceHeartbeat): void {
  useEffect(() => {
    if (classId === undefined) return;
    const tabId = resolveTabId();
    let stopped = false;

    const beat = async (): Promise<void> => {
      if (stopped) return;
      try {
        await mutateCore('presence.heartbeat', {
          tabId,
          classId,
          ...(appId === null ? {} : { appId }),
          route,
        });
      } catch {
        // Voir le commentaire de tête : un battement perdu ne doit rien casser.
      }
    };

    // Immédiatement, puis à intervalle : changer d'app doit se voir sur l'écran de l'enseignant
    // tout de suite, pas trente secondes plus tard.
    void beat();
    const timer = setInterval(() => {
      void beat();
    }, HEARTBEAT_MS);

    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [classId, appId, route]);
}
